import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Btn } from '../../kit';
import { AdminTarget } from '../../adminNav';
import { Check, Modal } from '../emailKit';
import { LIFECYCLE_LABEL } from '../../tags/shared';
import { BodyFormat, TriggerTemplateOption } from '../content/types';

/**
 * What the MEMAC tab's views share (backend: Backend/memac.py, section
 * "email", actions memac_*): the payload shapes — read defensively, because
 * a field the server leaves out must never read as "on" — the spec's
 * grouping and wording, and the confirm dialog every switch-on goes through.
 *
 * The safety model the UI mirrors (contract, 2026-10-11): an automatic
 * MEMAC mail goes out only when ALL of these are open —
 *   1. the server's env gate MEMAC_AUTO=1 (read-only here),
 *   2. the global switch (app_settings memac.enabled, Settings),
 *   3. the trigger's own switch: On + mode Auto (owner memac).
 * Everything is seeded off/manual. Live mails sent by other code (welcome,
 * trial sweep, payment mails) are owner "external": listed, never sent here.
 */

export const SECTION = 'email';

/** Template groups the MEMAC library is filed under (email_templates DEFAULTS). */
export const MEMAC_GROUPS = [
  'MEMAC · User activity', 'MEMAC · New data', 'MEMAC · Features', 'MEMAC · Explorer',
  'MEMAC · Trial', 'MEMAC · Trial lapsed', 'MEMAC · Voyager',
];
export const MEMAC_KEY_PREFIX = 'memac_';
/** Saved templates (the library shared with Campaigns, Email Center → Saved templates). */
export const LIBRARY_KEY_PREFIX = 'lib_';

export type Flash = { kind: 'error' | 'ok'; text: string } | null;

// ---------------------------------------------------------------------------
// Small readers
// ---------------------------------------------------------------------------
export const num = (v: any) => Number(v ?? 0).toLocaleString();
const str = (v: any): string => (v === null || v === undefined ? '' : String(v));
/** null for "not set" — '' / null / NaN — so a missing cap never reads as 0. */
export const intOrNull = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
/** A JSONB column as an object, whether it arrives parsed or as a string. */
export const obj = (v: any): Record<string, any> => {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim().startsWith('{')) {
    try { const o = JSON.parse(v); return o && typeof o === 'object' && !Array.isArray(o) ? o : {}; } catch { return {}; }
  }
  return {};
};
/** A list from a list, a JSON array string or a comma-separated string. */
export const list = (v: any): string[] => {
  if (v === null || v === undefined || v === '') return [];
  if (Array.isArray(v)) return v.map(x => String(x ?? '').trim()).filter(Boolean);
  const s = String(v).trim();
  if (s.startsWith('[')) { try { return list(JSON.parse(s)); } catch { /* fall through */ } }
  return s.split(',').map(x => x.trim()).filter(Boolean);
};
const bool = (v: any): boolean => v === true || v === 1 || v === '1' || v === 'true';
/** A server flag when it sent one, else the derived value. */
const flag = (v: any, derived: boolean): boolean => (typeof v === 'boolean' ? v : derived);

/** Dates as the business reads them: IST. */
export const fmtIST = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
  });
};

// ---------------------------------------------------------------------------
// Vocabulary (server lists win; these are the fallbacks and the order)
// ---------------------------------------------------------------------------
export const CATEGORY_FALLBACK: { key: string; label: string }[] = [
  { key: 'service', label: 'Service' },
  { key: 'user_activity', label: 'User activity' },
  { key: 'new_data', label: 'New data' },
  { key: 'features', label: 'New features & upgrades' },
  { key: 'marketing', label: 'Marketing & promotions' },
  { key: 'industry_news', label: 'Industry news' },
  { key: 'billing', label: 'Billing' },
];

export const COHORT_FALLBACK: { key: string; label: string; definition: string }[] = [
  { key: 'A', label: 'A — Dormant', definition: 'No search run, profile 50% or less' },
  { key: 'B', label: 'B — Curious, unprofiled', definition: 'Has searched, profile 50% or less' },
  { key: 'C', label: 'C — Ready, unengaged', definition: 'Profile over 50%, no search run' },
  { key: 'D', label: 'D — Warm', definition: 'Has searched, profile over 50%' },
];

/** The spec's tables, in the spec's order: §5 by category, §6 by status. */
export const SPEC_TABLES: { prefix: string; label: string }[] = [
  { prefix: 'UA', label: 'User activity' },
  { prefix: 'ND', label: 'New data' },
  { prefix: 'NF', label: 'New features & upgrades' },
  { prefix: 'MK', label: 'Marketing & promotions' },
  { prefix: 'IN', label: 'Industry news' },
  { prefix: 'BL', label: 'Billing — Razorpay & UTech' },
  { prefix: 'EX', label: 'Explorer (free)' },
  { prefix: 'TA', label: 'Voyager trial active' },
  { prefix: 'TL', label: 'Voyager trial lapsed' },
  { prefix: 'VP', label: 'Voyager paid / assigned' },
  { prefix: 'X', label: 'Custom triggers (made here)' },
];
export const specPrefix = (id: string): string => {
  const p = String(id || '').split('-')[0].toUpperCase();
  return SPEC_TABLES.some(t => t.prefix === p) ? p : 'X';
};

/** Where an external mail's switch lives, by its external_ref. */
export const EXTERNAL_SWITCHES: { match: RegExp; where: string; link?: AdminTarget }[] = [
  { match: /trial_lifecycle/i, where: 'Billing → Promotions & trials → Trial lifecycle (“auto”)',
    link: { group: 'billing', tab: 'promos' } },
  { match: /razorpay/i, where: 'Razorpay dashboard (customer notifications)' },
];

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------
export type ParamSpec = { name: string; type: string; default: any; help: string; options?: string[] };
export type Signal = { key: string; label: string; params: ParamSpec[]; manualOnly: boolean; help: string };
export type Settings = {
  enabled: boolean; interval_minutes: number | null; window: [number, number] | null;
  status_cap_30d: number | null; max_per_run: number | null; exclude_unsubscribed: boolean;
};
export type Overview = {
  statuses: { key: string; label: string; count: number | null }[];
  cohorts: { key: string; label: string; definition: string; count: number | null }[];
  matrix: { status: string; cohort: string; count: number }[];
  settings: Settings;
  envAuto: boolean;
  scheduler: { running: boolean | null; last_run_at: string | null; next_run_at: string | null };
  categories: { key: string; label: string }[];
  signals: Signal[];
  counts: { triggers: number | null; enabled: number | null; auto: number | null };
  raw: any;
};

const toParam = (p: any): ParamSpec | null => {
  if (!p) return null;
  if (typeof p === 'string') return { name: p, type: 'string', default: '', help: '' };
  const name = str(p.name || p.key);
  if (!name) return null;
  return {
    name, type: str(p.type || '').toLowerCase(), default: p.default, help: str(p.help || p.description || ''),
    options: Array.isArray(p.options) ? p.options.map(String) : undefined,
  };
};

const toSettings = (s: any): Settings => {
  const w = Array.isArray(s?.send_window_ist) ? s.send_window_ist : null;
  const window: [number, number] | null =
    w && w.length >= 2 && Number.isFinite(Number(w[0])) && Number.isFinite(Number(w[1]))
      ? [Number(w[0]), Number(w[1])] : null;
  return {
    // Only an explicit true reads as on.
    enabled: s?.enabled === true,
    interval_minutes: intOrNull(s?.interval_minutes),
    window,
    status_cap_30d: intOrNull(s?.status_cap_30d),
    max_per_run: intOrNull(s?.max_per_run),
    // Missing reads as the safe default (unsubscribed members left out).
    exclude_unsubscribed: s?.exclude_unsubscribed !== false,
  };
};

export const normOverview = (d: any): Overview => {
  const cats = Array.isArray(d?.categories) && d.categories.length
    ? d.categories.map((c: any) => typeof c === 'string' ? { key: c, label: c }
      : Array.isArray(c) ? { key: str(c[0]), label: str(c[1] ?? c[0]) } : { key: str(c.key), label: str(c.label || c.key) })
    : CATEGORY_FALLBACK;
  const cohorts = Array.isArray(d?.cohorts) && d.cohorts.length
    ? d.cohorts.map((c: any) => ({
      key: str(c.key), label: str(c.label || c.key), definition: str(c.definition || c.description || ''),
      count: intOrNull(c.count),
    }))
    : COHORT_FALLBACK.map(c => ({ ...c, count: null }));
  return {
    statuses: (Array.isArray(d?.statuses) ? d.statuses : []).map((s: any) => ({
      key: str(s.key), label: str(s.label || LIFECYCLE_LABEL[s.key] || s.key), count: intOrNull(s.count),
    })),
    cohorts,
    matrix: (Array.isArray(d?.matrix) ? d.matrix : []).map((m: any) => ({
      status: str(m.status ?? m.lifecycle), cohort: str(m.cohort), count: Number(m.count ?? m.n ?? 0) || 0,
    })),
    settings: toSettings(d?.settings),
    envAuto: d?.env_auto === true,
    scheduler: {
      running: typeof d?.scheduler?.running === 'boolean' ? d.scheduler.running : null,
      last_run_at: d?.scheduler?.last_run_at || null,
      next_run_at: d?.scheduler?.next_run_at || null,
    },
    categories: cats,
    signals: (Array.isArray(d?.signals) ? d.signals : []).map((s: any) => ({
      key: str(s.key), label: str(s.label || s.key), help: str(s.help || s.description || ''),
      params: (Array.isArray(s.params_schema) ? s.params_schema : Array.isArray(s.params) ? s.params : [])
        .map(toParam).filter(Boolean) as ParamSpec[],
      manualOnly: bool(s.manual_only) || s.key === 'broadcast' || s.auto_allowed === false,
    })),
    counts: {
      triggers: intOrNull(d?.counts?.triggers), enabled: intOrNull(d?.counts?.enabled), auto: intOrNull(d?.counts?.auto),
    },
    raw: d,
  };
};

export type ExternalSwitch = { on: boolean | null; where: string; link?: AdminTarget; note: string };

export type Trigger = {
  id: string; name: string; description: string; category: string; is_service: boolean; builtin: boolean;
  external: boolean; external_ref: string; ext: ExternalSwitch | null; templateKeys: string[];
  audience: Record<string, any>; signal: string; signal_params: Record<string, any>; stop_signal: string;
  content_mode: 'template' | 'design' | 'custom'; template_key: string; content: Record<string, any>;
  template_name: string; cta_label: string; primary_cta: string;
  max_per_30d: number | null; max_lifetime: number | null; cooldown_days: number | null; per_occurrence: number | null;
  mode: 'manual' | 'auto'; enabled: boolean; enabled_by: string; enabled_at: string | null;
  notes: string; updated_by: string; updated_at: string | null;
  would_match: number | null; would_match_at: string | null;
  last_run: any; last_run_at: string | null; last_sent_at: string | null; sent_30d: number | null;
  on_hold: boolean; hold_reason: string; manual_only: boolean; alias_of: string;
  /** Not switchable or editable here: external, or an alias of another trigger. */
  read_only: boolean;
  /** The server's verdicts when it sends them (memac._trigger_out); derived otherwise. */
  can_enable: boolean; can_send: boolean; can_test: boolean; can_delete: boolean; can_reset: boolean;
  template_missing: boolean;
  /** The template it sends (memac._template_info): its format, version and
   *  category, whether it is a saved one (shared with Campaigns, 'lib_…'),
   *  archived, or written for another category than the trigger's. */
  template_format: BodyFormat | null; template_version: number | null; template_category: string;
  template_library: boolean; template_archived: boolean; template_category_differs: boolean;
  /** An own copy: what it was started from, and whether that saved template has moved on since. */
  copy_source: CopySource | null; copy_stale: boolean;
  /** The spec's own "Applies to" words, and the spec table it sits in. */
  applies_to: string; spec_group: string; spec_group_label: string; signal_label: string;
  raw: any;
};

/** content.source: where a trigger's own copy was started from. */
export type CopySource = { kind: string; ref: string; name: string; version: number | null; at: string };
export type { TriggerTemplateOption };

const fmtName = (v: any): BodyFormat | null => (v === 'text' || v === 'html' || v === 'design' ? v : null);

const toCopySource = (v: any): CopySource | null => {
  const o = obj(v);
  if (!o.kind) return null;
  return { kind: str(o.kind), ref: str(o.ref), name: str(o.name || o.ref), version: intOrNull(o.version), at: str(o.at) };
};

export const toTemplateOption = (x: any): TriggerTemplateOption => ({
  key: str(x?.key),
  name: str(x?.name || x?.key),
  group: str(x?.group || (bool(x?.library) ? 'Saved templates' : 'Other')),
  category: str(x?.category || ''),
  library: bool(x?.library) || str(x?.key).startsWith(LIBRARY_KEY_PREFIX),
  format: fmtName(x?.format) || 'text',
  version: intOrNull(x?.version),
  primary_cta: str(x?.primary_cta || ''),
  primary_cta_url: str(x?.primary_cta_url || ''),
  // An older server sends no flag: do not warn about a plain-text mail then.
  send_html: typeof x?.send_html === 'boolean' ? x.send_html : true,
});

const extSwitch = (r: any, ref: string): ExternalSwitch => {
  // memac._trigger_out sends live_state {on, switch, where, detail}; `switch`
  // there is the setting's name, so it is only read as a state when it is
  // a boolean or an object.
  const live = r?.live_state && typeof r.live_state === 'object' ? r.live_state : null;
  const sw = live ?? (typeof r?.switch === 'boolean' || (r?.switch && typeof r.switch === 'object') ? r.switch : null)
    ?? r?.external_switch ?? r?.external_state ?? null;
  let on: boolean | null = null;
  if (typeof sw === 'boolean') on = sw;
  else if (sw && typeof sw === 'object') {
    const v = sw.on ?? sw.enabled ?? sw.auto ?? sw.state;
    if (typeof v === 'boolean') on = v;
    else if (v === 'on' || v === 'off') on = v === 'on';
  }
  if (on === null && typeof r?.external_on === 'boolean') on = r.external_on;
  if (on === null && typeof r?.live_on === 'boolean') on = r.live_on;
  const known = EXTERNAL_SWITCHES.find(x => x.match.test(ref));
  // A ref that names no known switch is the sending code, not a switch.
  const where = str((sw && typeof sw === 'object' && (sw.where || sw.label)) || r?.switch_where || r?.where
    || known?.where || `No switch — always sent${ref ? ` by ${ref}` : ''}`);
  return { on, where, link: known?.link,
    note: str((sw && typeof sw === 'object' && (sw.detail || sw.note)) || r?.external_note || '') };
};

export const normTrigger = (r: any): Trigger => {
  const id = str(r?.id ?? r?.trigger_id ?? r?.key);
  const owner = str(r?.owner || 'memac').toLowerCase();
  const ref = str(r?.external_ref || '');
  const content = obj(r?.content);
  const stop = r?.stop_signal;
  const lastRun = r?.last_run ?? null;
  const lastRunAt = lastRun && typeof lastRun === 'object'
    ? (lastRun.finished_at || lastRun.started_at || null) : (typeof lastRun === 'string' ? lastRun : null);
  const lastSent = r?.last_sent_at || r?.last_sent || (lastRun && typeof lastRun === 'object' && Number(lastRun.sent) > 0
    ? (lastRun.finished_at || lastRun.started_at) : null) || null;
  const mode = str(r?.content_mode || 'template');
  const signal = str(r?.signal || '');
  return {
    id,
    name: str(r?.name || id),
    description: str(r?.description || ''),
    category: str(r?.category || ''),
    // memac derives it from the category (service and billing are exempt).
    is_service: bool(r?.is_service) || ['service', 'billing'].includes(str(r?.category)),
    builtin: bool(r?.builtin),
    external: owner === 'external',
    external_ref: ref,
    ext: owner === 'external' ? extSwitch(r, ref) : null,
    templateKeys: list(r?.external_keys ?? r?.template_keys ?? r?.keys ?? (r?.template_key ? [r.template_key] : [])),
    audience: obj(r?.audience),
    signal,
    signal_params: obj(r?.signal_params),
    stop_signal: typeof stop === 'string' ? stop : str(obj(stop).signal || ''),
    content_mode: mode === 'design' || mode === 'custom' ? mode : 'template',
    template_key: str(r?.template_key || ''),
    content,
    template_name: str(r?.template_name || r?.template_key || ''),
    cta_label: str(r?.cta_label || ''),
    primary_cta: str(r?.primary_cta_label || r?.primary_cta || r?.template_cta || ''),
    max_per_30d: intOrNull(r?.max_per_30d),
    max_lifetime: intOrNull(r?.max_lifetime),
    cooldown_days: intOrNull(r?.cooldown_days),
    per_occurrence: intOrNull(r?.per_occurrence),
    // Only the exact words count: anything else reads as the safe state.
    mode: r?.mode === 'auto' ? 'auto' : 'manual',
    enabled: r?.enabled === true,
    enabled_by: str(r?.enabled_by || ''),
    enabled_at: r?.enabled_at || null,
    notes: str(r?.notes || ''),
    updated_by: str(r?.updated_by || ''),
    updated_at: r?.updated_at || null,
    would_match: intOrNull(r?.would_match),
    would_match_at: r?.would_match_at || null,
    last_run: lastRun,
    last_run_at: lastRunAt,
    last_sent_at: lastSent,
    sent_30d: intOrNull(r?.sent_30d),
    on_hold: bool(r?.on_hold) || bool(r?.hold) || r?.status === 'on_hold' || signal === 'on_hold' || signal === 'hold',
    manual_only: bool(r?.manual_only) || signal === 'broadcast' || r?.auto_allowed === false,
    alias_of: str(r?.alias_of || ''),
    hold_reason: str(r?.hold_reason || ''),
    read_only: typeof r?.read_only === 'boolean' ? r.read_only : owner !== 'memac' || !!r?.alias_of,
    can_enable: flag(r?.can_enable, owner === 'memac' && !r?.alias_of && !bool(r?.on_hold)),
    can_send: flag(r?.can_send, owner === 'memac' && !r?.alias_of && !bool(r?.on_hold)),
    can_test: flag(r?.can_test, owner === 'memac' && !r?.alias_of && !bool(r?.on_hold)),
    can_delete: flag(r?.can_delete, owner === 'memac' && !bool(r?.builtin)),
    can_reset: flag(r?.can_reset, owner === 'memac' && bool(r?.builtin) && !r?.alias_of),
    template_missing: bool(r?.template_missing),
    template_format: fmtName(r?.template_format),
    template_version: intOrNull(r?.template_version),
    template_category: str(r?.template_category || ''),
    template_library: bool(r?.template_library) || str(r?.template_key || '').startsWith(LIBRARY_KEY_PREFIX),
    template_archived: bool(r?.template_archived),
    template_category_differs: bool(r?.template_category_differs),
    copy_source: toCopySource(r?.copy_source ?? content.source),
    copy_stale: bool(r?.copy_stale),
    applies_to: str(r?.applies_to || ''),
    spec_group: str(r?.spec_group || ''),
    spec_group_label: str(r?.spec_group_label || ''),
    signal_label: str(r?.signal_label || ''),
    raw: r,
  };
};

/** The triggers list: memac rows plus the external (live elsewhere) ones,
 *  which may come in `triggers` (owner external), in `external`, or both;
 *  and the template picker's list (`templates`: MEMAC's own first, then the
 *  saved ones, then the other e-mails — the server's order). */
export const normTriggers = (d: any): {
  triggers: Trigger[]; razorpay: Record<string, any>[]; templates: TriggerTemplateOption[];
} => {
  const rows: Trigger[] = (Array.isArray(d?.triggers) ? d.triggers : Array.isArray(d) ? d : []).map(normTrigger);
  const byId = new Map(rows.map(t => [t.id, t]));
  (Array.isArray(d?.external) ? d.external : []).forEach((x: any) => {
    const t = normTrigger({ owner: 'external', ...x });
    const have = byId.get(t.id);
    if (have) {
      // Same trigger listed twice: keep the row, take the live switch state.
      const ref = t.external_ref || have.external_ref;
      have.external = true;
      have.external_ref = ref;
      have.ext = extSwitch({ ...have.raw, ...x }, ref);
      if (t.templateKeys.length) have.templateKeys = t.templateKeys;
    } else if (t.id) { rows.push(t); byId.set(t.id, t); }
  });
  const rz = Array.isArray(d?.razorpay_notices) ? d.razorpay_notices : [];
  return {
    triggers: rows.filter(t => t.id),
    razorpay: rz.map((n: any) => (typeof n === 'string' ? { notice: n } : n)),
    templates: (Array.isArray(d?.templates) ? d.templates : []).map(toTemplateOption).filter((x: TriggerTemplateOption) => x.key),
  };
};

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------
export const categoryLabel = (key: string, cats: { key: string; label: string }[]) =>
  cats.find(c => c.key === key)?.label || CATEGORY_FALLBACK.find(c => c.key === key)?.label || key || '—';

/** "Explorer · cohort C, D" — who a trigger is for, from its audience. */
export const appliesTo = (t: Trigger): string => t.applies_to || audienceText(t);
/** The audience filter in words (what the server actually applies). */
export const audienceText = (t: Trigger): string => {
  const a = t.audience || {};
  const lc = list(a.lifecycle ?? a.status ?? a.statuses);
  const co = list(a.cohort ?? a.cohorts);
  const parts = [lc.length ? lc.map(k => LIFECYCLE_LABEL[k] || k).join(', ') : 'Any status',
    co.length ? `cohort ${co.join(', ')}` : 'any cohort'];
  const extra = [...list(a.tags), ...list(a.tags_all)].length ? ' · tagged' : '';
  const not = list(a.not_tags).length ? ' · minus tags' : '';
  return `${parts.join(' · ')}${extra}${not}`;
};

/** The spec's "Max / month" column, from the caps. */
export const capText = (t: Pick<Trigger, 'max_per_30d' | 'max_lifetime' | 'cooldown_days' | 'per_occurrence'>): string => {
  const out: string[] = [];
  if (t.max_lifetime === 1) out.push('once');
  else if (t.max_lifetime) out.push(`${t.max_lifetime} in total`);
  if (t.per_occurrence) out.push(`${t.per_occurrence} per occurrence`);
  if (t.max_per_30d) out.push(`${t.max_per_30d} / 30 days`);
  if (t.cooldown_days) out.push(`1 per ${t.cooldown_days} days`);
  return out.join(' · ') || 'no cap';
};

export const windowText = (s: Settings): string =>
  s.window ? `${String(s.window[0]).padStart(2, '0')}:00–${String(s.window[1]).padStart(2, '0')}:00 IST` : 'any time of day';

/** The words for a state the page could not read. Always amber, never the
 *  green "nothing is sent": a status that failed to load says nothing about
 *  whether mail is going out. */
export const STATE_UNKNOWN = 'Status unknown — could not read MEMAC state';

/**
 * Why automatic sending is or is not happening, in one list. Empty = all
 * open. null = unknown: the MEMAC status was not read (or every gate it shows
 * is open and the number of On + Auto triggers is not known), so callers show
 * STATE_UNKNOWN — a closed gate is only claimed when the server said so.
 */
export const closedGates = (ov: Overview | null, autoOn: number | null): string[] | null => {
  if (!ov) return null;
  const out: string[] = [];
  const server = serverStep(ov);
  if (server) out.push(server.gate);
  if (!ov.settings.enabled) out.push('the global MEMAC switch is off (Settings)');
  if (autoOn === 0) out.push('no trigger is On with mode Auto');
  return out.length || autoOn !== null ? out : null;
};

/**
 * What the server still needs before anything can go out on its own (null =
 * nothing): the setting MEMAC_AUTO=1, which the API reads when it starts —
 * so setting it takes a restart — or, with it set, a restart because the
 * automatic runs have not started.
 */
export const serverStep = (ov: Overview | null): { gate: string; todo: string } | null => {
  if (!ov) return null;
  if (!ov.envAuto) {
    return { gate: 'the server setting MEMAC_AUTO=1 is missing (set it on the server, then restart the API)',
      todo: 'set MEMAC_AUTO=1 on the server, then restart the API' };
  }
  if (ov.scheduler.running === false) {
    return { gate: 'automatic runs have not started on the server (restart the API)',
      todo: 'restart the API so the automatic runs start' };
  }
  return null;
};

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
export const Pill: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'mute' | 'blue' | 'sky'; children: React.ReactNode; title?: string }> =
  ({ tone, children, title }) => {
  const look = tone === 'ok' ? 'text-green-700 bg-green-50 border-green-200'
    : tone === 'warn' ? 'text-amber-800 bg-amber-50 border-amber-200'
      : tone === 'bad' ? 'text-red-600 bg-red-50 border-red-200'
        : tone === 'blue' ? 'text-primary-og-600 bg-primary-100 border-primary-200'
          : tone === 'sky' ? 'text-sky-800 bg-sky-50 border-sky-200'
            : 'text-grayscale-1200 bg-grayscale-200 border-grayscale-200';
  return <span title={title} className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${look}`}>{children}</span>;
};

/** The state of one trigger as a pill: what it does right now. `gates` is
 *  closedGates() — null (unknown) reads amber, never as waiting or sending. */
export const StatePill: React.FC<{ t: Trigger; gates?: string[] | null }> = ({ t, gates }) => {
  if (t.external) {
    return t.ext?.on === false ? <Pill tone="mute" title={t.ext.where}>Live elsewhere · switched off</Pill>
      : <Pill tone="blue" title={t.ext?.where}>Live elsewhere{t.ext?.on ? ' · on' : ''}</Pill>;
  }
  if (t.alias_of) return <Pill tone="mute" title={`The same mail as ${t.alias_of} — switch that one`}>= {t.alias_of}</Pill>;
  if (t.on_hold) return <Pill tone="mute" title={t.hold_reason}>On hold</Pill>;
  if (!t.enabled) return <Pill tone="mute">Off</Pill>;
  if (t.mode === 'auto') {
    if (gates === null) {
      return <Pill tone="warn" title={`${STATE_UNKNOWN} — it may be sending automatically`}>On · auto (status unknown)</Pill>;
    }
    return gates?.length ? <Pill tone="warn" title="Armed, but a global gate is closed — nothing is sent yet">On · auto (waiting)</Pill>
      : <Pill tone="bad" title="Sends automatically on every run">On · auto</Pill>;
  }
  return <Pill tone="ok" title="Sent only when someone presses Send">On · manual</Pill>;
};

/** A dialog rendered at the end of <body>: opened from a table cell it would
 *  otherwise inherit the cell's nowrap and sit in the row's stacking context
 *  (rows of switched-off triggers are faded), so later rows painted over it. */
export const inBody = (node: React.ReactElement) =>
  createPortal(<div className="whitespace-normal">{node}</div>, document.body);

/** "11 Oct, 00:40" (the year only when it is not this year), IST — for narrow cells. */
export const fmtShortIST = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const year = (x: Date) => x.toLocaleString('en-GB', { year: 'numeric', timeZone: 'Asia/Kolkata' });
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    ...(year(d) === year(new Date()) ? {} : { year: 'numeric' as const }),
  });
};

/**
 * The confirm every switch-on goes through. `lines` say exactly what will
 * start happening; `ack` (when given) must be ticked before the button works
 * — used whenever members would start getting mail without anyone pressing
 * Send.
 */
export const ConfirmDialog: React.FC<{
  title: string; lines: React.ReactNode[]; confirmLabel: string; danger?: boolean; ack?: string;
  busy?: boolean; error?: string; onConfirm: () => void; onClose: () => void;
}> = ({ title, lines, confirmLabel, danger, ack, busy, error, onConfirm, onClose }) => {
  const [ok, setOk] = useState(false);
  const ready = !busy && (!ack || ok);
  return inBody(
    <Modal title={title} onClose={onClose}
      footer={<>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn kind={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={!ready}>
          {busy ? 'Working…' : confirmLabel}
        </Btn>
      </>}>
      <div className="text-xs font-semibold uppercase tracking-wide text-grayscale-1100">What will happen</div>
      <ul className="list-disc pl-5 flex flex-col gap-1">
        {lines.filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
      </ul>
      {ack && <div className="pt-1"><Check checked={ok} onChange={setOk}>{ack}</Check></div>}
      {error && <div className="text-xs text-red-700">{error}</div>}
    </Modal>,
  );
};

/** A notice strip (the "nothing is sent automatically" banner and friends). */
export const Banner: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'info'; children: React.ReactNode }> = ({ tone, children }) => {
  const look = tone === 'ok' ? 'border-green-200 bg-green-50 text-green-800'
    : tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-900'
      : tone === 'bad' ? 'border-red-200 bg-red-50 text-red-800'
        : 'border-primary-200 bg-primary-100 text-grayscale-1300';
  return <div className={`rounded-lg border px-3 py-2 text-sm ${look}`}>{children}</div>;
};

/** Switched on in the panel — the global switch and at least one On + Auto
 *  trigger — while the server is not ready yet (serverStep). */
export const waitingForServer = (ov: Overview | null, autoOn: number | null): boolean =>
  !!ov && ov.settings.enabled && (autoOn ?? 0) > 0 && serverStep(ov) !== null;

/** The go-live banner for that case: what is on, and the one step left. */
export const ServerWaitBanner: React.FC<{ ov: Overview; ids: string[]; onOpenTrigger?: (id: string) => void }> =
  ({ ov, ids, onOpenTrigger }) => {
  const step = serverStep(ov);
  if (!step) return null;
  const s = ov.settings;
  const delay = intOrNull(ov.raw?.settings?.startup_delay_minutes);
  return (
    <Banner tone="info">
      <b>Switched on, but nothing is sent yet.</b> MEMAC is on and{' '}
      {ids.length ? ids.map((id, i) => <React.Fragment key={id}>{i > 0 && ', '}
        {onOpenTrigger
          ? <button type="button" className="underline underline-offset-2" onClick={() => onOpenTrigger(id)}>{id}</button>
          : id}
      </React.Fragment>) : 'a trigger'} {ids.length > 1 ? 'are' : 'is'} On + Auto, but one step is left, on the
      server: <b>{step.todo}</b>. Until then nothing goes out on its own; dry runs, tests and “Send to these N now” work
      as usual.
      {' '}After the restart, the first automatic run comes {delay ?? 5} min later, then every
      {' '}{s.interval_minutes ?? '?'} min, {windowText(s)}.
    </Banner>
  );
};
